import { toJsDate } from "../utils/toJsDate";
import React from "react";
import { type ActivityRequest, ActivityCategory } from "../types";
import { motion } from "framer-motion";
import {
  ClockIcon,
  LockIcon,
  MapPinIcon,
  UsersIcon,
  ChevronRightIcon,
  UserPlusIcon,
  HistoryIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  Trash2Icon,
  XIcon,
  UserXIcon,
  StarIcon,
} from "./icons/FeatherIcons";
import { CATEGORIES } from "../constants";
import { fallbackImage } from "../utils/getCachedImage";
import { getCategoryDefaultImage } from "../utils/activityUtils";
import { useCachedImage } from "../hooks/useCachedImage";

interface ActivityListItemProps {
  activity: ActivityRequest;
  onClick: () => void;
  showJoinButton?: boolean | undefined;
  showUndeleteButton?: boolean | undefined;
  originLabels?: string[];
  onJoin?: (activityId: string) => void | Promise<void>;
  onUndelete?: (activityId: string) => Promise<void>;
  isJoining?: boolean;
  isUndeleting?: boolean;
  hideImage?: boolean;
  currentUserId?: string;
  passedActivityIds?: string[];
  showPendingInviteBadge?: boolean;
  blurDetails?: boolean;
  showOrganizerIndicator?: boolean;
  hiddenStatusLabels?: string[];
}

const handleImageError =
  (activity: ActivityRequest) =>
  (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.currentTarget;
    const currentFallback = img.getAttribute("data-fallback-level") || "0";
    const seed = activity.id || activity.title || activity.category;

    // First try category-specific default image
    if (currentFallback === "0") {
      img.setAttribute("data-fallback-level", "1");
      img.src = getCategoryDefaultImage(activity.category, seed);
      return;
    }

    // If category image also fails, use basic fallback
    if (currentFallback === "1") {
      img.setAttribute("data-fallback-level", "2");
      img.src = fallbackImage;
    }

    // Stop here - no more fallbacks to prevent infinite loops
  };

const formatDate = (dateString: string) => {
  return toJsDate(dateString).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const timeAgo = (dateInput: any) => {
  const date = toJsDate(dateInput);
  if (!date || isNaN(date.getTime())) return "N/A";
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return `${Math.floor(interval)}y ago`;
  interval = seconds / 2592000;
  if (interval > 1) return `${Math.floor(interval)}mo ago`;
  interval = seconds / 86400;
  if (interval > 1) return `${Math.floor(interval)}d ago`;
  interval = seconds / 3600;
  if (interval > 1) return `${Math.floor(interval)}h ago`;
  interval = seconds / 60;
  if (interval > 1) return `${Math.floor(interval)}m ago`;
  return `${Math.floor(seconds)}s ago`;
};

const timeAgoPlain = (dateInput: any) => {
  const date = toJsDate(dateInput);
  if (!date || isNaN(date.getTime())) return "N/A";
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return `${Math.floor(interval)}y ago`;
  interval = seconds / 2592000;
  if (interval > 1) return `${Math.floor(interval)}mo ago`;
  interval = seconds / 86400;
  if (interval > 1) return `${Math.floor(interval)}d ago`;
  interval = seconds / 3600;
  if (interval > 1) return `${Math.floor(interval)}h ago`;
  interval = seconds / 60;
  if (interval > 1) return `${Math.floor(interval)}m ago`;
  return `${Math.floor(seconds)}s ago`;
};

// Clean title by removing extra whitespace and HTML entities
const cleanTitle = (title: string) => {
  return title
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
};

const ActivityListItem: React.FC<ActivityListItemProps> = ({
  activity,
  onClick,
  showJoinButton = false,
  showUndeleteButton = false,
  originLabels,
  onJoin,
  onUndelete,
  isJoining = false,
  isUndeleting = false,
  hideImage = false,
  currentUserId,
  passedActivityIds = [],
  showPendingInviteBadge = false,
  blurDetails = false,
  showOrganizerIndicator = false,
  hiddenStatusLabels = [],
}) => {
  const handleJoinClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the main onClick
    if (onJoin && !isJoining) {
      void onJoin(activity.id);
    }
  };

  const handleUndeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the main onClick
    if (onUndelete && !isUndeleting) {
      await onUndelete(activity.id);
    }
  };

  // Determine user's status with this activity
  const isCreator = currentUserId && activity.creatorId === currentUserId;
  const isParticipant =
    currentUserId &&
    activity.participantIds?.includes(currentUserId) &&
    !isCreator;
  const wasKickedOut =
    currentUserId && activity.kickedParticipantIds?.includes(currentUserId);
  const isDeclined =
    currentUserId && passedActivityIds.includes(activity.id) && !wasKickedOut;
  const organizerName = isCreator ? "You" : activity.creator?.username;
  const shouldShowOrganizer = showOrganizerIndicator && Boolean(organizerName);
  const participantCount = activity.participants?.length ?? 0;

  // Use activity imageUrl, or fallback to category default
  const categorySeed = activity.id || activity.title || activity.category;
  const imageSource =
    activity.imageUrl || getCategoryDefaultImage(activity.category, categorySeed);

  // Cache the activity image
  const cachedImage = useCachedImage(imageSource, activity.title, activity.id);

  const cleanedTitle = cleanTitle(activity.title);
  const categoryInfo = CATEGORIES.find((c) => c.value === activity.category);
  const hiddenStatusSet = new Set(hiddenStatusLabels);

  const renderTag = (
    key: string,
    label: string,
    Icon: React.ComponentType<{ className?: string }>,
    className: string,
    title: string,
  ) => (
    <div
      key={key}
      className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${className}`}
      title={title}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </div>
  );

  const normalizedOriginLabels = Array.from(new Set(originLabels || []));

  // Make image bigger when no button is shown below it
  const hasButtonBelowImage = showJoinButton || showUndeleteButton;
  const imageSize = hasButtonBelowImage ? "w-20 h-20" : "w-24 h-24";

  return (
    <motion.div
      className={`w-full text-left bg-white rounded-xl p-3 shadow-sm hover:bg-gray-50 transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${blurDetails ? "relative" : ""}`}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ type: "spring", stiffness: 250, damping: 30 }}
    >
      {/* Main content section */}
      <div className="flex items-center space-x-4">
        {/* Left column: Image and restore/join button */}
        {!hideImage && (
          <div className="flex flex-col items-center space-y-3 flex-shrink-0">
            <button
              onClick={onClick}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg"
              aria-label={`View details for ${cleanedTitle}`}
            >
              <img
                src={cachedImage}
                alt={cleanedTitle}
                onError={handleImageError(activity)}
                className={`${imageSize} object-cover rounded-lg`}
              />
            </button>
            {/* Restore button positioned below the image */}
            {showUndeleteButton && (
              <button
                onClick={handleUndeleteClick}
                disabled={isUndeleting}
                className="flex items-center justify-center px-2 py-1 bg-green-600 text-white text-xs font-semibold rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-20"
                title="Restore this activity"
              >
                {isUndeleting ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <HistoryIcon className="w-3 h-3 mr-1" />
                    Restore
                  </>
                )}
              </button>
            )}
            {/* Join button positioned below the image for declined activities */}
            {showJoinButton && (
              <button
                onClick={handleJoinClick}
                disabled={
                  isJoining ||
                  (Array.isArray(activity.participants)
                    ? activity.participants.length >= activity.groupSize
                    : false)
                }
                className="flex items-center justify-center px-2 py-1 bg-primary text-white text-xs font-semibold rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-20"
                title="Join this activity"
              >
                {isJoining ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <UserPlusIcon className="w-4 h-3 mr-1" />
                    {Array.isArray(activity.participants) &&
                    activity.participants.length >= activity.groupSize
                      ? "Full"
                      : "Join"}
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Right section: Content and actions */}
        <button
          onClick={onClick}
          className="flex items-center space-x-4 flex-1 min-w-0"
          aria-label={`View details for ${cleanedTitle}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-bold text-gray-900 truncate text-left">
                {cleanedTitle}
              </p>
              {normalizedOriginLabels.map((label) => {
                if (label === "Created") {
                  return renderTag(
                    `origin-${label}`,
                    "Created",
                    StarIcon,
                    "bg-blue-100 text-blue-700",
                    "Created by profile user",
                  );
                }
                if (label === "Deleted") {
                  return renderTag(
                    `origin-${label}`,
                    "Deleted",
                    Trash2Icon,
                    "bg-red-100 text-red-700",
                    "This activity was deleted",
                  );
                }
                if (label === "Expired") {
                  return renderTag(
                    `origin-${label}`,
                    "Expired",
                    ClockIcon,
                    "bg-gray-100 text-gray-700",
                    "This activity has ended",
                  );
                }
                return renderTag(
                  `origin-${label}`,
                  label,
                  StarIcon,
                  "bg-blue-100 text-blue-700",
                  label,
                );
              })}
              {!hiddenStatusSet.has("Reported") && activity.isReported &&
                renderTag(
                  "status-reported",
                  "Reported",
                  AlertTriangleIcon,
                  "bg-yellow-100 text-yellow-700",
                  "This activity is reported",
                )}
              {isParticipant && !hiddenStatusSet.has("Joined") && (
                renderTag(
                  "status-joined",
                  "Joined",
                  CheckCircleIcon,
                  "bg-green-100 text-green-700",
                  "You've joined this activity",
                )
              )}
              {isDeclined && !hiddenStatusSet.has("Declined") && (
                renderTag(
                  "status-declined",
                  "Declined",
                  XIcon,
                  "bg-rose-100 text-rose-600",
                  "You've declined this activity",
                )
              )}
              {wasKickedOut && !hiddenStatusSet.has("Removed") && (
                renderTag(
                  "status-removed",
                  "Removed",
                  UserXIcon,
                  "bg-red-100 text-red-700",
                  "You were removed from this activity",
                )
              )}
              {showPendingInviteBadge && !hiddenStatusSet.has("Pending") && (
                renderTag(
                  "status-pending",
                  "Pending",
                  ClockIcon,
                  "bg-amber-100 text-amber-700",
                  "Pending invitation - accept to join",
                )
              )}
              {activity.isPrivate && !hiddenStatusSet.has("Private") && (
                renderTag(
                  "status-private",
                  "Private",
                  LockIcon,
                  "bg-purple-100 text-purple-700",
                  "Private activity - invitation only",
                )
              )}
            </div>
            <div
              className={`mt-0.2 space-y-1.5 text-xs text-gray-500 ${blurDetails ? "filter blur-sm select-none" : ""}`}
            >
              {shouldShowOrganizer && (
                <div className="flex items-center">
                  <div className="flex items-center gap-1 bg-blue-50/60 text-blue-700/80 text-[11px] font-medium px-2 py-0.5 rounded-full max-w-full">
                    <StarIcon className="w-3 h-3 text-blue-500/80" />
                    <span className="truncate">
                      Created by {organizerName}{" "}
                      {timeAgoPlain(activity.createdAt)}
                    </span>
                  </div>
                </div>
              )}
              {!shouldShowOrganizer && (
                <div className="flex items-center">
                  <span className="text-left text-[11px] text-gray-500">
                    Created {timeAgo(activity.createdAt)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-start">
                <ClockIcon className="w-3.5 h-3.5 mr-2 text-primary/80" />
                <span className="text-left">
                  {formatDate(activity.dateTime)}
                </span>
              </div>
              <div className="flex items-center">
                <MapPinIcon className="w-3.5 h-3.5 mr-2 text-primary/80" />
                <span className="truncate">{activity.location}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <UsersIcon className="w-3.5 h-3.5 mr-2 text-primary/80" />
                  <span>
                    {activity.participants?.length ?? 0} / {activity.groupSize}{" "}
                    Joined
                  </span>
                </div>
                {categoryInfo && (
                  <div className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded flex items-center">
                    <categoryInfo.Icon className="w-3 h-3 mr-1" />
                    <span>{categoryInfo.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </button>
      </div>
    </motion.div>
  );
};

export default React.memo(ActivityListItem);
